import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UnitOfMeasure } from '../types/material.types';

const UNIT_OF_MEASURE_OPTIONS: { value: UnitOfMeasure; label: string }[] = [
  { value: 'UND', label: 'Unidad' },
  { value: 'KG', label: 'Kilogramo' },
  { value: 'G', label: 'Gramo' },
  { value: 'LB', label: 'Libra' },
  { value: 'LT', label: 'Litro' },
  { value: 'ML', label: 'Mililitro' },
  { value: 'GAL', label: 'Galon' },
  { value: 'MT', label: 'Metro' },
  { value: 'CM', label: 'Centimetro' },
  { value: 'IN', label: 'Pulgada' },
  { value: 'FT', label: 'Pie' },
  { value: 'M2', label: 'Metro cuadrado' },
  { value: 'M3', label: 'Metro cubico' },
  { value: 'BOX', label: 'Caja' },
  { value: 'ROLL', label: 'Rollo' },
  { value: 'SET', label: 'Juego/Set' },
  { value: 'PAR', label: 'Par' },
  { value: 'HR', label: 'Hora' },
  { value: 'DAY', label: 'Dia' },
  { value: 'MONTH', label: 'Mes' },
];

interface ProductFieldsFormValues {
  sku?: string | null;
  brand?: string | null;
  model?: string | null;
  unitOfMeasure?: UnitOfMeasure | null | '';
  weight?: number | null;
  width?: number | null;
  height?: number | null;
  depth?: number | null;
  [key: string]: unknown;
}

interface ProductFieldsProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<ProductFieldsFormValues>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  isLoading?: boolean;
}

export function ProductFields({
  register,
  errors,
  setValue,
  watch,
  isLoading,
}: ProductFieldsProps) {
  const unitOfMeasure = watch('unitOfMeasure');

  return (
    <div className="space-y-4">
      <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Datos del Producto
      </p>

      {/* SKU, Marca, Modelo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="sku">SKU</Label>
          <Input id="sku" placeholder="SKU-001" {...register('sku')} disabled={isLoading} />
          {errors.sku && <p className="text-xs text-destructive">{String(errors.sku.message)}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="brand">Marca</Label>
          <Input
            id="brand"
            placeholder="Marca del producto"
            {...register('brand')}
            disabled={isLoading}
          />
          {errors.brand && (
            <p className="text-xs text-destructive">{String(errors.brand.message)}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="model">Modelo</Label>
          <Input
            id="model"
            placeholder="Modelo del producto"
            {...register('model')}
            disabled={isLoading}
          />
          {errors.model && (
            <p className="text-xs text-destructive">{String(errors.model.message)}</p>
          )}
        </div>
      </div>

      {/* Unidad de medida, Peso, Ancho, Alto */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>Unidad de Medida</Label>
          <Select
            value={unitOfMeasure || ''}
            onValueChange={(value) =>
              setValue('unitOfMeasure', value === '__none__' ? null : (value as UnitOfMeasure))
            }
            disabled={isLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Sin asignar</SelectItem>
              {UNIT_OF_MEASURE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.value} — {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="weight">Peso (kg)</Label>
          <Input
            id="weight"
            type="number"
            step="0.001"
            min="0"
            placeholder="0.000"
            {...register('weight', { valueAsNumber: true })}
            disabled={isLoading}
          />
          {errors.weight && (
            <p className="text-xs text-destructive">{String(errors.weight.message)}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="width">Ancho (cm)</Label>
          <Input
            id="width"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            {...register('width', { valueAsNumber: true })}
            disabled={isLoading}
          />
          {errors.width && (
            <p className="text-xs text-destructive">{String(errors.width.message)}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="height">Alto (cm)</Label>
          <Input
            id="height"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            {...register('height', { valueAsNumber: true })}
            disabled={isLoading}
          />
          {errors.height && (
            <p className="text-xs text-destructive">{String(errors.height.message)}</p>
          )}
        </div>
      </div>

      {/* Profundidad */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label htmlFor="depth">Profundidad (cm)</Label>
          <Input
            id="depth"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            {...register('depth', { valueAsNumber: true })}
            disabled={isLoading}
          />
          {errors.depth && (
            <p className="text-xs text-destructive">{String(errors.depth.message)}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductFields;
